/**
 * Count-up animation for Statistics Row.
 * Shared duration so 70, 12k, and 500 finish together.
 */
const DURATION_MS = 1600;
const COMPACT_MULTIPLIERS = { k: 1000, m: 1000000 };

function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
}

function parseStat(raw) {
    const original = String(raw || '').trim();
    const match = original.match(/^([\d.,]+)\s*([km])?\s*([+]*)?$/i);

    if (!match) {
        return null;
    }

    const numeric = parseFloat(match[1].replace(/,/g, ''));

    if (!Number.isFinite(numeric)) {
        return null;
    }

    const compact = (match[2] || '').toLowerCase();
    const suffix = match[3] || '';
    const multiplier = compact ? COMPACT_MULTIPLIERS[compact] : 1;

    return {
        original,
        compact,
        suffix,
        endValue: numeric * multiplier,
    };
}

function formatCompact(current, parsed) {
    const divisor = COMPACT_MULTIPLIERS[parsed.compact];
    const value = current / divisor;
    const rounded = Math.round(value * 10) / 10;
    const display = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);

    return `${display}${parsed.compact}${parsed.suffix}`;
}

function formatValue(current, parsed, progress) {
    if (progress >= 1) {
        return parsed.original;
    }

    if (parsed.compact) {
        return formatCompact(current, parsed);
    }

    return `${Math.round(current)}${parsed.suffix}`;
}

function getRowStats(row) {
    const els = Array.from(row.querySelectorAll('.stats-row__number'));
    const parsedList = els.map((el) => parseStat(el.getAttribute('data-stats-end') || el.textContent));

    return { els, parsedList };
}

function prepareRow(els, parsedList) {
    els.forEach((el, index) => {
        const parsed = parsedList[index];

        if (!parsed) {
            return;
        }

        el.style.minWidth = `${parsed.original.length}ch`;
        el.textContent = formatValue(0, parsed, 0);
    });
}

function animateRow(els, parsedList) {
    const start = performance.now();

    function frame(now) {
        const progress = Math.min(1, (now - start) / DURATION_MS);
        const eased = easeOutCubic(progress);

        els.forEach((el, index) => {
            const parsed = parsedList[index];

            if (!parsed) {
                return;
            }

            el.textContent = formatValue(parsed.endValue * eased, parsed, progress);
        });

        if (progress < 1) {
            window.requestAnimationFrame(frame);
        }
    }

    window.requestAnimationFrame(frame);
}

function initRow(row) {
    if (row.dataset.statsInit === '1') {
        return;
    }

    row.dataset.statsInit = '1';

    const { els, parsedList } = getRowStats(row);

    if (!parsedList.some(Boolean)) {
        return;
    }

    if (prefersReducedMotion()) {
        return;
    }

    prepareRow(els, parsedList);

    const start = () => animateRow(els, parsedList);

    if (!('IntersectionObserver' in window)) {
        start();
        return;
    }

    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (!entry.isIntersecting) {
                return;
            }

            observer.disconnect();
            start();
        });
    }, {
        threshold: 0.4,
    });

    observer.observe(row);
}

function initStatsCountUp() {
    document.querySelectorAll('.stats-row').forEach(initRow);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStatsCountUp);
} else {
    initStatsCountUp();
}
